import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PageLayout } from '../../components/organisms/PageLayout';
import { ProfileMenu, ProfileMenuItem } from '../../components/organisms/ProfileMenu';
import BasicInfoTab from '../../components/molecules/profile/tabs/BasicInfoTab';
import PreferencesTab from '../../components/molecules/profile/tabs/PreferencesTab';
import ExperienceTab from '../../components/molecules/profile/tabs/ExperienceTab';
import VolunteerExperienceTab from '../../components/molecules/profile/tabs/VolunteerExperienceTab';
import CertificationsTab from '../../components/molecules/profile/tabs/CertificationsTab';
import EducationTab from '../../components/molecules/profile/tabs/EducationTab';
import ProjectsTab from '../../components/molecules/profile/tabs/ProjectsTab';
import LinksTab from '../../components/molecules/profile/tabs/LinksTab';
import { API_ENDPOINTS } from '../../config';
import { api } from '../../utils/api';
import '../../styles/profile-page.css';

interface Project {
  uid: string;
  project_name: string;
  headline: string;
  description: string | null;
  release_date: string | null;
  url: string | null;
  skills: string[] | null;
  achievements: string[] | null;
  gallery_urls: string[] | null;
  created_at: string;
  updated_at: string;
}

interface UserData {
  basic_info: BasicInfo | null;
  preferences: Preferences | null;
  experience: WorkExperience[] | null;
  volunteer_experience: VolunteerExperience[] | null;
  certifications: Certification[] | null;
  education: Education[] | null;
  projects: Project[] | null;
  urls: UserLink[] | null;
  created_at: string;
  updated_at: string;
}

interface Certification {
  uid: string;
  certification_name: string;
  organization_name: string | null;
  issued_at: string;
  expires_at: string | null;
  credential_id: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface Education {
  uid: string;
  institution_name: string;
  degree: string | null;
  field_of_study: string | null;
  graduation_date: string | null;
  description: string | null;
  grade: string | null;
  achievements: string[] | null;
  created_at: string;
  updated_at: string;
}

interface UserLink {
  uid: string;
  type: string;
  handle: string | null;
  custom_handle: {
    title: string;
    url: string;
    description?: string;
  } | null;
  created_at: string;
  updated_at: string;
}

interface VolunteerExperience {
  uid: string;
  organization_name: string;
  position: string;
  skills: string[] | null;
  description: string | null;
  achievements: string[] | null;
  start_date: string;
  end_date: string | null;
  location: string | null;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

interface WorkExperience {
  uid: string;
  employer_name: string;
  job_title: string;
  skills: string[] | null;
  description: string | null;
  achievements: string[] | null;
  start_date: string;
  end_date: string | null;
  location: string | null;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

interface BasicInfo {
  uid: string | null;
  first_name: string | null;
  last_name: string | null;
  headline: string | null;
  current_location: string | null;
  username: string | null;
  skills: string[] | null;
  emails: string | null;
  phones: string | null;
  bio: string | null;
}

interface Preferences {
  uid: string | null;
  primary_role_preference: string | null;
  primary_role_experience: string | null;
  other_roles_preferences: string[] | null;
  industries_preferences: string[] | null;
  exact_salary: number | null;
  salary_currency: string | null;
  experience_level: string | null;
  job_types: string[] | null;
  open_to_work_environment: string[] | null;
  preferred_work_environment: string | null;
  location_preferences: string[] | null;
  willing_to_relocate: boolean | null;
}

const Profile: React.FC = () => {
  const { tab } = useParams<{ tab: ProfileMenuItem }>();
  const activeTab = tab || 'basic-info';
  
  useEffect(() => {
    document.title = 'OfferFlow | Profile';
  }, []);
  
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await api.get(API_ENDPOINTS.users.getByUid);
        setUserData(response);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const breadcrumbItems = [
    { label: "Profile", href: "/profile" },
    { label: "Builder" }
  ];

  const getTabLabel = (tabKey: ProfileMenuItem): string => {
    const labels: Record<ProfileMenuItem, string> = {
      'basic-info': 'Basic Info',
      'preferences': 'Preferences',
      'experience': 'Experience',
      'volunteer-experience': 'Volunteer Experience',
      'certifications': 'Certifications',
      'education': 'Education',
      'projects': 'Projects',
      'links': 'Links',
    };
    return labels[tabKey] || 'Tab';
  };

  const renderTabContent = () => {
    if (loading) {
      return <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>;
    }

    switch (activeTab) {
      case 'basic-info':
        return (
          <BasicInfoTab 
            userData={userData}
            basicInfo={userData?.basic_info || null}
          />
        );
      case 'preferences':
        return (
          <PreferencesTab 
            userData={userData}
            preferences={userData?.preferences || null}
          />
        );
      case 'experience':
        return (
          <ExperienceTab 
            userData={userData}
            experiences={userData?.experience || []}
          />
        );
      case 'volunteer-experience':
        return (
          <VolunteerExperienceTab 
            userData={userData}
            experiences={userData?.volunteer_experience || []}
          />
        );
      case 'certifications':
        return (
          <CertificationsTab 
            userData={userData}
            certifications={userData?.certifications || []}
          />
        );
      case 'education':
        return (
          <EducationTab 
            userData={userData}
            education={userData?.education || []}
          />
        );
      case 'projects':
        return (
          <ProjectsTab 
            userData={userData}
            projects={userData?.projects || []}
          />
        );
      case 'links':
        return (
          <LinksTab 
            userData={userData}
            urls={userData?.urls || []}
          />
        );
      default:
        return (
          <BasicInfoTab 
            userData={userData}
            basicInfo={userData?.basic_info || null}
          />
        );
    }
  };

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <div className="profile-page">
        <div className="profile-page__sidebar">
          <ProfileMenu />
        </div>
        <div className="profile-page__content">
          {renderTabContent()}
        </div>
      </div>
    </PageLayout>
  );
};

export default Profile;
